/**
 * ============================================================================
 * RiskAssessmentDrawer — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: slide-in drawer (anchored to inline-end) that surfaces the
 *          supplier's risk assessment history with a sparkline of computed
 *          scores. Opened from any card in the Kanban.
 *
 * A11Y: role="dialog" + aria-modal, focus moves to the close button on open,
 *  Escape closes.
 * ============================================================================ */

import { useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './RiskRankingDashboard.module.css';
import {
  useRiskAssessmentHistoryQuery,
  type RiskRankingCard,
} from './useRiskRankingQuery';

export interface RiskAssessmentDrawerProps {
  card: RiskRankingCard;
  onClose: () => void;
}

export function RiskAssessmentDrawer({ card, onClose }: RiskAssessmentDrawerProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const histQ = useRiskAssessmentHistoryQuery(card.supplier_id);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const history = histQ.data?.history ?? [];

  return (
    <div
      className={styles.drawerBackdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={styles.drawerHeader}>
          <div>
            <h2 id={titleId} className={styles.drawerTitle}>
              {card.name}
            </h2>
            <p id={descId} className={styles.drawerSubtitle}>
              {t('supplier.riskRanking.drawerSubtitle', {
                defaultValue:
                  'Risk assessment history. Most recent first. Sparkline shows score trend.',
              })}
            </p>
          </div>
          <Button
            ref={closeRef}
            variant="tertiary"
            size="sm"
            onClick={onClose}
            aria-label={t('supplier.riskRanking.drawerCloseAria', {
              defaultValue: 'Close risk drawer',
            })}
          >
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        </header>

        <div className={styles.drawerBody}>
          {histQ.isLoading && (
            <div role="status" aria-live="polite">
              {[0, 1, 2].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {histQ.isError && (
            <div role="alert" className={styles.errorBanner}>
              {t('supplier.riskRanking.historyError', {
                defaultValue: 'Could not load history.',
              })}
            </div>
          )}

          {!histQ.isLoading && !histQ.isError && history.length === 0 && (
            <p className={styles.drawerEmpty}>
              {t('supplier.riskRanking.historyEmpty', {
                defaultValue: 'No history yet.',
              })}
            </p>
          )}

          {history.length > 0 && (
            <>
              <HistorySparkline values={history.map((h) => h.computed_score)} />
              <ul className={styles.historyList}>
                {history.map((h) => (
                  <li key={h.assessment_id} className={styles.historyItem}>
                    <header className={styles.historyHeader}>
                      <time dateTime={h.ts_utc} className={styles.historyTs}>
                        {dateFmt.format(new Date(h.ts_utc))}
                      </time>
                      <span className={styles.historyVersion}>
                        {h.formula_version}
                      </span>
                    </header>
                    <div className={styles.historyScoreRow}>
                      <span className={styles.historyScore}>
                        {nf.format(h.computed_score)}
                      </span>
                      <span
                        className={`${styles.historyCat} ${styles[`cat-${h.category}`] ?? ''}`}
                      >
                        {t(`supplier.risk.${h.category}`, {
                          defaultValue: h.category,
                        })}
                      </span>
                    </div>
                    {h.rationale && (
                      <p className={styles.historyRationale}>{h.rationale}</p>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * HistorySparkline — inline SVG. Mirrors the look in KpiTilesGrid.
 * --------------------------------------------------------------------------- */

function HistorySparkline({ values }: { values: number[] }) {
  const { t } = useTranslation();
  const gradId = useId();
  if (values.length === 0) return null;

  const W = 420;
  const H = 96;
  const PAD = 6;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const range = hi - lo === 0 ? 1 : hi - lo;
  // History is most-recent-first; we walk left→right oldest→newest.
  const ordered = [...values].reverse();
  const step = (W - PAD * 2) / Math.max(ordered.length - 1, 1);
  const coords = ordered.map((v, i) => ({
    x: PAD + i * step,
    y: H - PAD - ((v - lo) / range) * (H - PAD * 2),
  }));
  const d = coords
    .map((c, i) =>
      i === 0 ? `M${c.x.toFixed(2)},${c.y.toFixed(2)}` : `L${c.x.toFixed(2)},${c.y.toFixed(2)}`,
    )
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      role="img"
      aria-label={t('supplier.riskRanking.historySparklineAria', {
        defaultValue: 'Score trend across {{n}} assessments (range {{min}}–{{max}})',
        n: values.length,
        min: lo,
        max: hi,
      })}
      className={styles.historySpark}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1DD1A1" />
          <stop offset="50%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#EF4444" />
        </linearGradient>
      </defs>
      <path
        d={d}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={2} fill={`url(#${gradId})`} />
      ))}
    </svg>
  );
}
